import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { purchaseItemService, PurchaseItemDTO } from '../../services/purchaseItemService';
import '../../pages/Products/ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const PurchaseItemListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<PurchaseItemDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const productId = searchParams.get('productId');

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (productId) {
        try {
          response = await purchaseItemService.getPurchaseItemsByProduct(parseInt(productId));
          const items = response?.data?.data;
          if (Array.isArray(items)) {
            setItems(items);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Failed to load product-specific purchase items:', err);
          // If product-specific items fail, fall through to load all items
        }
      }
      // Load all items if no productId or product-specific call failed
      response = await purchaseItemService.getPurchaseItems(1, 100);
      const allItems = response?.data?.data?.data ?? response?.data?.data;
      if (Array.isArray(allItems)) {
        setItems(allItems);
      }
      setLoading(false);
    } catch (err) {
      setError(t('purchaseItem.failedToLoadItem'));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      let response;
      if (productId) {
        try {
          response = await purchaseItemService.getPurchaseItemsByProduct(parseInt(productId));
          const items = response?.data?.data;
          if (Array.isArray(items)) {
            setItems(items);
            setRefreshing(false);
            return;
          }
        } catch (err) {
          console.error('Failed to load product-specific purchase items:', err);
          // If product-specific items fail, fall through to load all items
        }
      }
      // Load all items if no productId or product-specific call failed
      response = await purchaseItemService.getPurchaseItems(1, 100);
      const allItems = response?.data?.data?.data ?? response?.data?.data;
      if (Array.isArray(allItems)) {
        setItems(allItems);
      }
    } catch (err) {
      setError(t('purchaseItem.failedToLoadItem'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'productSKU',
      label: t('purchaseItem.productSKU'),
      sortable: true,
      width: '130px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'productName',
      label: t('purchaseItem.productName'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'supplierName',
      label: t('purchaseItem.supplierName'),
      sortable: true,
      width: '180px',
    },
    {
      key: 'purchasePrice',
      label: t('purchaseItem.purchasePrice'),
      sortable: true,
      width: '130px',
      render: (value, row) => (
        <strong>{Number(value).toFixed(2)} {row.currency}</strong>
      ),
    },
    {
      key: 'minimumOrderQuantity',
      label: 'MOQ',
      sortable: true,
      width: '80px',
      hideTablet: true,
    },
    {
      key: 'leadTimeDays',
      label: t('purchaseItem.leadTimeDays'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      render: (value) => `${value} days`,
    },
    {
      key: 'taxName',
      label: t('purchaseItem.tax'),
      width: '140px',
      hideTablet: true,
      render: (_value, row) =>
        row.taxPercentage != null && row.taxPercentage > 0
          ? `${row.taxPercentage}%`
          : <span style={{ color: 'var(--ims-text-muted)' }}>—</span>,
    },
    {
      key: 'isActive',
      label: t('purchaseItem.status'),
      width: '100px',
      hideMobile: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('purchaseItem.active') : t('purchaseItem.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/purchase-items/${item.purchaseItemId}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: (
        <SquarePen size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/purchase-items/${item.purchaseItemId}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: (
        <Trash2 size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/purchase-items/${item.purchaseItemId}/delete`),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  return (
    <Layout>
      <div className="container">
        {productId && (
          <div className="alert" style={{
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #93c5fd',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <span>
              <strong>Filter Active:</strong> Showing purchase items for Product ID: {productId}
            </span>
            <button
              onClick={() => navigate('/purchase-items')}
              style={{
                background: 'none',
                border: 'none',
                color: '#1e40af',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                padding: '0 8px'
              }}
              title="Clear filter"
            >
              ✕
            </button>
          </div>
        )}

        <div className="header">
          <h1 className="title">{t('purchaseItem.title')}</h1>
          <div className="header-actions">
            <button
              className="btn-new"
              onClick={() => navigate('/purchase-items/create')}
            >
              <CirclePlus/>              
              {t('purchaseItem.newItem')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('purchaseItem.refreshItems')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('purchaseItem.refreshing') : t('purchaseItem.refreshItems')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('purchaseItem.noItemsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default PurchaseItemListPage;
